import React, { useMemo } from 'react';
import { Progress } from 'antd';
import { Empty } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { apiV2HighEnergyDeviceAnalysisEnergyUsageRateBarChartPost } from '@maxtropy/device-customer-apis-v2';
import { keepTwoOrLine } from '@/utils/utils';
import { DateType } from '../TimeSelector';
import styles from './index.module.scss';

export interface TimeParams {
  startTime: number;
  endTime: number;
  timeResolution: DateType;
}

export interface UsageRankItem {
  rank: number;
  name: string;
  usageRate?: number;
}

export interface UsageRankCardProps {
  timeParams: TimeParams;
}

const UsageRankCard: React.FC<UsageRankCardProps> = ({ timeParams }) => {
  const { startTime, endTime, timeResolution } = timeParams;

  const { data } = useRequest(
    () =>
      apiV2HighEnergyDeviceAnalysisEnergyUsageRateBarChartPost({
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        timeResolution,
      }),
    {
      ready: !!startTime && !!endTime,
      refreshDeps: [startTime, endTime, timeResolution],
    }
  );

  const rankData: UsageRankItem[] = useMemo(() => {
    return (data?.barChartData ?? []).map((item, index) => ({
      rank: index + 1,
      name: item.deviceName ?? '--',
      usageRate: item.usageRate,
    }));
  }, [data]);

  const getRankClassName = (rank: number) => {
    switch (rank) {
      case 1:
        return styles.rankFirst;
      case 2:
        return styles.rankSecond;
      case 3:
        return styles.rankThird;
      default:
        return styles.rankOther;
    }
  };

  const isEmpty = !rankData || rankData.length === 0;

  return (
    <div className={styles.usageRankCard}>
      <div className={styles.header}>
        <span className={styles.label}>高能耗设备使用率排名</span>
      </div>
      <div className={styles.content}>
        {isEmpty ? (
          <div className={styles.emptyContent}>
            <Empty />
          </div>
        ) : (
          rankData.map(item => (
            <div className={styles.rankItem} key={item.rank}>
              <div className={`${styles.rankNum} ${getRankClassName(item.rank)}`}>{item.rank}</div>
              <span className={styles.deviceName}>{item.name}</span>
              <Progress
                className={styles.progress}
                percent={item.usageRate}
                strokeColor="#16DD8E"
                trailColor="rgba(255,255,255,0.2)"
                size="small"
                showInfo={false}
              />
              <span className={styles.usageLabel}>使用率：</span>
              <span className={styles.usageText}>{keepTwoOrLine(item.usageRate)}%</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default UsageRankCard;
